import React, { useState, useCallback, useMemo } from "react";

// Utils
import areObjectsEqual from '../../../../utils/areObjectsEqual';

// Constants
const MAX_RANK_LENGTH = 4;

const INITIAL_ERRORS = {
  rank: false,
  colorHexCode: false,
  checkRank: false,
};

// API Service
const backgroundAPI = {
  checkRankUnique: async (rank, artPackId, id) => {
    const res = await fetch(`/api/artist/artpack/checkrank/${rank}/${artPackId}/${id}`);
    if (res.status === 200) {
      const data = await res.json();
      return data.checkStatus;
    }
    return false;
  },
};


const Form = ({ handleSubmit, formData, onDiscard, typeCheck, artPack }) => {
  const [form, setForm] = useState(formData);
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return !areObjectsEqual(formData, form);
  }, [formData, form]);

  // Check if rank is unique
  const checkRankUnique = useCallback(async (rank, artPackId, id) => {
    const isRankTaken = await backgroundAPI.checkRankUnique(rank, artPackId, id);
    setErrors(prev => ({ ...prev, checkRank: isRankTaken }));
    return isRankTaken;
  }, []);

  // Prevent negative sign input
  const handleKeyDown = useCallback((e) => {
    if (e.key === "-") {
      e.preventDefault();
    }
  }, []);

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    // Limit rank to 4 digits
    const processedValue = name === "rank" ? value.slice(0, MAX_RANK_LENGTH) : value;

    setForm(prev => ({ ...prev, [name]: processedValue }));
  }, []);

  // Validate form data
  const validateForm = useCallback(() => {
    const newErrors = {
      rank: !form.rank,
      colorHexCode: !form.color_hex_code,
      checkRank: false,
    };

    setErrors(newErrors);
    return !newErrors.rank && !newErrors.colorHexCode;
  }, [form]);

  // Handle form submission
  const handleFormSubmit = useCallback(async () => {
    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check if rank is unique
    const id = typeCheck === 'updateItem' ? form.id : 0;
    const isRankTaken = await checkRankUnique(form.rank, artPack?.art_pack_id, id);
    if (isRankTaken) {
      return;
    }

    // Submit form
    handleSubmit(form);
    onDiscard();
  }, [validateForm, typeCheck, form, checkRankUnique, artPack?.art_pack_id, handleSubmit, onDiscard]);

  // Determine if save button should be disabled
  const isSaveDisabled = useMemo(() => {
    return !hasUnsavedChanges;
  }, [hasUnsavedChanges]);


  return (
    <>
      {/* Rank Input Field */}
      <div className="mt-4">
        <label htmlFor="rank" className="form-labels">Rank</label>
        <input
          type="number"
          name="rank"
          id="rank"
          onChange={handleChange}
          value={form.rank}
          className="form-control inputs"
          min={0}
          max={9999}
          onKeyDown={handleKeyDown}
        />
        {errors.rank && (
          <span className="form-text text-danger">
            Rank is required
          </span>
        )}
        {errors.checkRank && (
          <span className="form-text text-danger">
            This rank is already assigned. Please select a different one
          </span>
        )}
      </div>

      {/* Background Hex Color Input Field */}
      <div className="row mt-5">
        <div className="form-labels">Background Hex Color</div>
        <div className="col-10 col-sm-2">
          <input
            type="color"
            name="color_hex_code"
            id="color_hex_code"
            value={form.color_hex_code}
            onChange={handleChange}
            className="form-control mt-2 inputs"
          />
        </div>
        {errors.colorHexCode && (
          <span className="form-text text-danger">
            Background Color Hex is required
          </span>
        )}
      </div>

      {/* Action Buttons */}
      <div className="pt-5">
        <div className="btn btn-secondary form-btn me-4" onClick={onDiscard}>
          Discard
        </div>
        <input
          type="submit"
          value="Save Changes"
          className="btn bg-orange form-btn"
          onClick={handleFormSubmit}
          disabled={isSaveDisabled}
        />
      </div>
    </>
  );
};

export default Form;
